import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useTenant from 'hooks/useTenant';
import LogoMain from './LogoMain';

interface TenantLogoProps {
  reverse?: boolean;
  width?: number | string;
  height?: number | string;
  showText?: boolean;
  sx?: any;
}

const TenantLogo: React.FC<TenantLogoProps> = ({
  reverse,
  width = 200,
  height = 100,
  showText = false,
  sx,
  ...others
}) => {
  const theme = useTheme();
  const { tenant, isLoading } = useTenant();

  const calculatedHeight = React.useMemo(() => {
    if (height === 'auto') {
      return 100;
    }
    if (width === 52 || width === '52px') {
      return Math.round(52 / 2.72);
    }
    return height;
  }, [width, height]);

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', ...sx }}
        {...others}
      >
        <LogoMain
          reverse={reverse}
          width={width}
          height={calculatedHeight}
        />
      </Box>
    );
  }

  if (!tenant) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', ...sx }}
        {...others}
      >
        <LogoMain
          reverse={reverse}
          width={width}
          height={calculatedHeight}
        />
      </Box>
    );
  }

  return (
    <TenantLogoWithFallback
      tenant={tenant}
      reverse={reverse}
      width={width}
      height={calculatedHeight}
      showText={showText}
      sx={sx}
      theme={theme}
      {...others}
    />
  );
};

const TenantLogoWithFallback: React.FC<{
  tenant: any;
  reverse?: boolean;
  width: number | string;
  height: number | string;
  showText?: boolean;
  sx?: any;
  theme: any;
  [key: string]: any;
}> = ({ tenant, reverse, width, height, showText, sx, theme, ...others }) => {
  const [imageError, setImageError] = React.useState(false);

  if (imageError || !tenant.logo) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', ...sx }}
        {...others}
      >
        <LogoMain
          reverse={reverse}
          width={width}
          height={height}
        />
        {showText && tenant.name && (
          <Typography
            variant="h6"
            sx={{
              ml: 2,
              color: tenant.primaryColor || theme.palette.primary.main,
              fontWeight: 'bold'
            }}
          >
            {tenant.name}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', ...sx }}
      {...others}
    >
      <img
        src={tenant.logo}
        alt={`${tenant.name || 'Tenant'} Logo`}
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
          maxWidth: '100%',
          objectFit: 'contain',
          filter: reverse ? 'invert(1)' : 'none'
        }}
        onError={() => {
          console.warn('Erro ao carregar logo do tenant:', tenant.logo);
          console.log('TenantLogo: Mudando para fallback LogoMain');
          setImageError(true);
        }}
      />
      {showText && tenant.name && (
        <Typography
          variant="h6"
          sx={{
            ml: 2,
            color: tenant.primaryColor || theme.palette.primary.main,
            fontWeight: 'bold'
          }}
        >
          {tenant.name}
        </Typography>
      )}
    </Box>
  );
};

export default TenantLogo;
